import type { SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

import { GlassModal } from "../components/GlassModal";
import { useFocusTrap } from "../components/useFocusTrap";
import { useOverlayShell } from "../components/useOverlayShell";
import {
  dutyActivityReportCopy, fetchDutyActivityReportWeek, revalidateDutyActivityReportWeek,
  type DutyActivityKey, type DutyActivityReadResult, type DutyActivityReport,
} from "../data/duty-activity-report";
import { fetchProofOfServiceTargets, type ProofTargetsResult } from "../data/proof-of-service";
import { generateClientArtifactPdf } from "../lib/client-artifact-capture";
import { scheduleTimesheetsPath } from "../lib/schedule-timesheets-route";
import { formatServiceWeekRange, isServiceWeekStart, latestCompletedServiceWeekStart } from "../lib/service-week";
import { useSupportSession } from "../support/SupportSessionProvider";
import { DutyActivityReportSheet } from "./DutyActivityReportSheet";
import "../styles/proof-of-service-dialog.css";

// Same four shipped permission keys as the proof of service. The duty activity report
// introduces no new permission and no support-aware proxy; lock O-01 (Internal variant)
// stays unbuilt, so this surface only ever asks the host for the client variant.
const REQUIRED_PERMISSIONS = ["report.view", "schedule.view", "evidence.view", "timeclock.manage"] as const;

type GuidedLink = { label: string; href: string };
type StatusMessage = { text: string; link?: GuidedLink };
type Outcome = Exclude<DutyActivityReadResult, { status: "ok" }> | { status: "changed" };

function resultCopy(result: Outcome): string {
  if (result.status === "changed") return dutyActivityReportCopy.changed;
  if (result.status === "refused") return dutyActivityReportCopy[result.reason];
  if (result.reason === "too_large") return dutyActivityReportCopy.too_large;
  if (result.reason === "source_incomplete") return dutyActivityReportCopy.source_incomplete;
  return dutyActivityReportCopy.unavailable;
}

function guidedLinkFor(result: Outcome, key: DutyActivityKey | null): GuidedLink | undefined {
  if (!key) return undefined;
  const href = scheduleTimesheetsPath({ clientId: key.clientId, siteId: key.siteId, weekStart: key.weekStart });
  if (result.status === "refused" && result.reason === "week_unapproved") return { label: dutyActivityReportCopy.approveInTimesheets, href };
  if (result.status === "unavailable" && result.reason === "source_incomplete") return { label: dutyActivityReportCopy.openInTimesheets, href };
  return undefined;
}

type Props = {
  client: SupabaseClient;
  actorId: string | null;
  accountId: string;
  accountRole: string | null;
  can: (permission: string) => boolean;
};

/** The UI gate prevents inappropriate reads; each host RPC independently authorizes the caller. */
export function DutyActivityReportExport(props: Props) {
  const support = useSupportSession();
  const gate = useRef({ props, support });
  gate.current = { props, support };
  const canExportNow = useCallback(() => {
    const current = gate.current;
    return Boolean(current.props.actorId) && Boolean(current.props.accountId)
      && (current.props.accountRole === "admin" || current.props.accountRole === "supervisor")
      && REQUIRED_PERMISSIONS.every((permission) => current.props.can(permission))
      && !current.support.loading && current.support.session === null;
  }, []);
  if (!canExportNow()) return null;
  return <DutyActivityExportWorkspace key={JSON.stringify([props.actorId, props.accountId, props.accountRole])}
    client={props.client} accountId={props.accountId} canExportNow={canExportNow} />;
}

type Target = Extract<ProofTargetsResult, { status: "ok" }>["targets"][number];
type Retained = { key: string; revision: string; report: DutyActivityReport };
type Busy = "targets" | "preview" | "revalidate" | "download" | null;

/** Canonical local comparison only. This key and the revision never enter a URL or the DOM.
 *  The media choice is part of the key, so toggling it invalidates any retained sheet. */
function requestKey(key: DutyActivityKey): string {
  return JSON.stringify([key.accountId, key.clientId, key.siteId, key.weekStart, key.variant,
    "incidents", "checkpointExceptions", "coverageSummary", key.includeMediaCounts ? "media" : "no-media"]);
}

function DutyActivityExportWorkspace({ client, accountId, canExportNow }: {
  client: SupabaseClient; accountId: string; canExportNow: () => boolean;
}) {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<ProofTargetsResult | null>(null);
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [weekStart, setWeekStart] = useState("");
  const [includeMedia, setIncludeMedia] = useState(true);
  const [retained, setRetained] = useState<Retained | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [message, setMessage] = useState<StatusMessage | null>(null);
  const [printReady, setPrintReady] = useState(false);
  const navigate = useNavigate();
  const generation = useRef(0);
  const mounted = useRef(false);
  const openRef = useRef(false);
  const keyRef = useRef<string | null>(null);
  const retainedRef = useRef<Retained | null>(null);
  const busyRef = useRef<Busy>(null);
  const printReadyRef = useRef(false);
  const previewRoot = useRef<HTMLDivElement | null>(null);
  const downloadUrls = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const opener = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDivElement>(null);

  const setPending = useCallback((value: Busy) => { busyRef.current = value; setBusy(value); }, []);
  const clearDownloads = useCallback(() => {
    for (const [url, timer] of downloadUrls.current) {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
    }
    downloadUrls.current.clear();
  }, []);
  const clearSheet = useCallback(() => {
    retainedRef.current = null;
    printReadyRef.current = false;
    setRetained(null);
    setPrintReady(false);
    clearDownloads();
  }, [clearDownloads]);
  const invalidate = useCallback(() => {
    generation.current += 1;
    clearSheet();
    setPending(null);
    setMessage(null);
  }, [clearSheet, setPending]);

  const showResult = useCallback((result: Outcome, forKey: DutyActivityKey | null) => {
    const link = guidedLinkFor(result, forKey);
    setMessage(link ? { text: resultCopy(result), link } : { text: resultCopy(result) });
  }, []);

  useLayoutEffect(() => {
    mounted.current = true;
    invalidate();
    openRef.current = false;
    setOpen(false);
    const { data } = client.auth.onAuthStateChange(() => {
      // A replacement session can retain the same actor/account/role. Invalidate synchronously
      // in the auth callback so a late revalidation cannot print before React commits a render.
      openRef.current = false;
      keyRef.current = null;
      invalidate();
      setOpen(false);
      setTargets(null);
    });
    return () => {
      data.subscription.unsubscribe();
      mounted.current = false;
      openRef.current = false;
      generation.current += 1;
      retainedRef.current = null;
      clearDownloads();
    };
  }, [client, invalidate, clearDownloads]);

  const rows = targets?.status === "ok" ? targets.targets : [];
  const clients = Array.from(new Map(rows.map((target) => [target.clientId, target])).values());
  const sites = rows.filter((target) => target.clientId === clientId);
  const site = sites.find((target) => target.siteId === siteId) ?? null;
  const key: DutyActivityKey | null = site && isServiceWeekStart(weekStart)
    ? { accountId, clientId: site.clientId, siteId: site.siteId, weekStart, variant: "client", includeMediaCounts: includeMedia } : null;
  keyRef.current = key ? requestKey(key) : null;
  useFocusTrap(dialog, open && retained === null);

  const isCurrent = (attempt: number, expectedKey: string | null) => mounted.current && openRef.current
    && canExportNow() && generation.current === attempt && keyRef.current === expectedKey;

  async function openDialog() {
    if (!canExportNow() || openRef.current) return;
    invalidate();
    openRef.current = true;
    setOpen(true);
    setTargets(null);
    setClientId(""); setSiteId(""); setWeekStart(""); setIncludeMedia(true); keyRef.current = null;
    setPending("targets");
    const attempt = generation.current;
    let next: ProofTargetsResult;
    try { next = await fetchProofOfServiceTargets(client, accountId); }
    catch { next = { status: "unavailable", reason: "target_unavailable" }; }
    if (!isCurrent(attempt, null)) return;
    setTargets(next);
    setPending(null);
  }

  function closeDialog() {
    openRef.current = false;
    invalidate();
    setOpen(false);
    setTargets(null);
    keyRef.current = null;
    opener.current?.focus();
  }

  function changeClient(value: string) {
    invalidate(); keyRef.current = null;
    setClientId(value); setSiteId(""); setWeekStart("");
  }
  function changeSite(value: string) {
    invalidate(); keyRef.current = null;
    const next: Target | undefined = sites.find((target) => target.siteId === value);
    setSiteId(value);
    setWeekStart(next ? latestCompletedServiceWeekStart(next.timeZone) ?? "" : "");
  }
  function changeWeek(value: string) { invalidate(); keyRef.current = null; setWeekStart(value); }
  function changeMedia(value: boolean) { invalidate(); keyRef.current = null; setIncludeMedia(value); }

  async function preview() {
    if (!key || busyRef.current !== null || !canExportNow() || !openRef.current) return;
    invalidate();
    const expectedKey = requestKey(key);
    keyRef.current = expectedKey;
    const attempt = generation.current;
    setPending("preview");
    let next: DutyActivityReadResult;
    try { next = await fetchDutyActivityReportWeek(client, key); }
    catch { next = { status: "unavailable", reason: "unavailable" }; }
    if (!isCurrent(attempt, expectedKey)) return;
    setPending(null);
    if (next.status !== "ok") { showResult(next, key); return; }
    const sheet = { key: expectedKey, revision: next.revision, report: next.report };
    retainedRef.current = sheet;
    setRetained(sheet);
  }

  const sheetReady = useCallback((ready: boolean) => {
    printReadyRef.current = ready;
    setPrintReady(ready);
  }, []);

  async function download() {
    const sheet = retainedRef.current;
    const root = previewRoot.current;
    if (!key || !sheet || !root || busyRef.current !== null || !printReadyRef.current || !canExportNow() || !openRef.current) return;
    const expectedKey = requestKey(key);
    if (sheet.key !== expectedKey || keyRef.current !== expectedKey) {
      invalidate();
      setMessage({ text: dutyActivityReportCopy.changed });
      return;
    }
    const attempt = ++generation.current;
    setPending("revalidate");
    const stillMatches = () => isCurrent(attempt, expectedKey) && retainedRef.current === sheet
      && retainedRef.current.key === expectedKey && retainedRef.current.revision === sheet.revision && printReadyRef.current
      && previewRoot.current === root && root.isConnected;
    const discardChanged = () => {
      if (mounted.current && generation.current === attempt) {
        invalidate();
        setMessage({ text: dutyActivityReportCopy.changed });
      }
    };
    try {
      const beforeCapture = await revalidateDutyActivityReportWeek(client, key, sheet.revision);
      if (!stillMatches()) { discardChanged(); return; }
      if (beforeCapture.status !== "current") { invalidate(); showResult(beforeCapture, key); return; }
      setPending("download");
      const pdf = await generateClientArtifactPdf(root, stillMatches);
      if (!stillMatches()) { discardChanged(); return; }
      // Capturing many pages may take time. Recheck remote approval/caller facts after capture,
      // immediately before releasing the same retained object's PDF to the browser.
      const beforeDownload = await revalidateDutyActivityReportWeek(client, key, sheet.revision);
      if (!stillMatches()) { discardChanged(); return; }
      if (beforeDownload.status !== "current") { invalidate(); showResult(beforeDownload, key); return; }
      const url = URL.createObjectURL(pdf);
      const timer = setTimeout(() => { URL.revokeObjectURL(url); downloadUrls.current.delete(url); }, 60_000);
      downloadUrls.current.set(url, timer);
      const link = document.createElement("a");
      link.href = url;
      link.download = `duty-activity-report-${key.weekStart}.pdf`;
      link.hidden = true;
      document.body.appendChild(link);
      try {
        // No await or state transition between this final caller/key/revision gate and release.
        if (!stillMatches()) {
          clearTimeout(timer); URL.revokeObjectURL(url); downloadUrls.current.delete(url);
          discardChanged(); return;
        }
        link.click();
      } finally { link.remove(); }
      setPending(null);
    } catch {
      if (!isCurrent(attempt, expectedKey)) { clearDownloads(); discardChanged(); return; }
      invalidate();
      setMessage({ text: dutyActivityReportCopy.unavailable });
    }
  }

  function backToSettings() { invalidate(); }
  function openTimesheetsGuide(href: string) { closeDialog(); navigate(href); }
  const targetMessage = targets?.status === "unavailable"
    ? targets.reason === "targets_too_large" ? dutyActivityReportCopy.targets_too_large : dutyActivityReportCopy.unavailable
    : targets?.status === "ok" && targets.targets.length === 0 ? dutyActivityReportCopy.noTargets : null;
  const statusText = message?.text ?? targetMessage
    ?? (busy === "targets" ? "Loading client sites..." : busy === "preview" ? "Loading duty activity report..." : null);

  return <>
    <button type="button" className="pos-button pos-button--secondary" ref={opener} onClick={() => void openDialog()}>Export duty activity report</button>
    <GlassModal open={open && retained === null} onClose={closeDialog} ariaLabel="Export duty activity report">
      <div className="pos-dialog" ref={dialog}>
        <header className="pos-dialog__head"><h2>Export duty activity report</h2><button type="button" className="pos-button pos-button--ghost" onClick={closeDialog} aria-label="Close duty activity report">Close</button></header>
        <p className="pos-dialog__note">One client, one site, one service week.</p>
        <label className="pos-dialog__field">Prepared for<select value={clientId} disabled={busy === "targets" || clients.length === 0} onChange={(event) => changeClient(event.target.value)}><option value="">Select a client</option>{clients.map((target) => <option key={target.clientId} value={target.clientId}>{target.clientLabel}</option>)}</select></label>
        <label className="pos-dialog__field">Site<select value={siteId} disabled={!clientId || sites.length === 0} onChange={(event) => changeSite(event.target.value)}><option value="">Select a site</option>{sites.map((target) => <option key={target.siteId} value={target.siteId}>{target.siteLabel}</option>)}</select></label>
        <label className="pos-dialog__field">Period<input type="date" value={weekStart} disabled={!site} onChange={(event) => changeWeek(event.target.value)} aria-describedby="dar-period-help" /></label>
        <p className="pos-dialog__note" id="dar-period-help">{site ? `${formatServiceWeekRange(weekStart) ?? "Choose a Monday"} · ${site.timeZone}` : "Monday through Sunday in the site's timezone."}</p>
        {weekStart && !isServiceWeekStart(weekStart) && <p role="status" className="pos-dialog__note">Choose a Monday to begin the service week.</p>}
        <fieldset className="pos-dialog__sections"><legend>Included sections</legend>
          <label><input type="checkbox" checked disabled />Incidents <span>Required</span></label>
          <label><input type="checkbox" checked disabled />Checkpoint exceptions <span>Required</span></label>
          <label><input type="checkbox" checked disabled />Coverage summary <span>Required</span></label>
          <label><input type="checkbox" checked={includeMedia} disabled={busy !== null} onChange={(event) => changeMedia(event.target.checked)} />Evidence counts per incident</label>
          <label><input type="checkbox" checked={false} disabled />Patrol rounds detail</label>
          <p>{dutyActivityReportCopy.laterRounds}</p>
          <p>{dutyActivityReportCopy.coverageElsewhere}</p>
        </fieldset>
        <p className="pos-dialog__note">The PDF contains the same pages shown in Preview. Download the PDF and send it yourself.</p>
        {statusText && <div role="status" className="pos-dialog__status">
          <p>{statusText}</p>
          {message?.link && <button type="button" className="pos-button pos-button--secondary" onClick={() => openTimesheetsGuide(message.link!.href)}>{message.link.label}</button>}
        </div>}
        <div className="pos-dialog__actions"><button type="button" className="pos-button pos-button--secondary" disabled={!key || busy !== null} onClick={() => void preview()}>Preview</button><button type="button" className="pos-button pos-button--primary" disabled>Download PDF</button></div>
        <p className="pos-dialog__note">{dutyActivityReportCopy.printHint}</p>
      </div>
    </GlassModal>
    {open && retained && <DutyActivityPreview report={retained.report} onReadyChange={sheetReady} onClose={backToSettings}
      onDownload={() => void download()} rootRef={previewRoot} downloadDisabled={!printReady || busy !== null} busy={busy === "revalidate" || busy === "download"} />}
  </>;
}

function DutyActivityPreview({ report, onReadyChange, onClose, onDownload, rootRef, downloadDisabled, busy }: {
  report: DutyActivityReport; onReadyChange: (ready: boolean) => void;
  onClose: () => void; onDownload: () => void; rootRef: RefObject<HTMLDivElement | null>; downloadDisabled: boolean; busy: boolean;
}) {
  const { panelRef } = useOverlayShell(true, false, onClose);
  useFocusTrap(panelRef, true);
  return createPortal(<div className="pos-preview-root" ref={(node) => { panelRef.current = node; rootRef.current = node; }} role="dialog" aria-modal="true" aria-label="Duty activity report preview" tabIndex={-1}>
    <div className="pos-preview-chrome">
      <div><strong>Duty activity report preview</strong><p>{dutyActivityReportCopy.printHint}</p></div>
      <div className="pos-preview-chrome__actions"><button type="button" className="pos-button pos-button--secondary" onClick={onClose}>Back to settings</button><button type="button" className="pos-button pos-button--primary" disabled={downloadDisabled} onClick={onDownload}>{busy ? "Preparing PDF..." : "Download PDF"}</button></div>
    </div>
    <div className="pos-preview-scroll"><DutyActivityReportSheet report={report} onReadyChange={onReadyChange} /></div>
  </div>, document.body);
}
